import React, { useState, useMemo } from 'react';
import {
  Sparkles, ChevronDown, ChevronUp, Layers, Lightbulb, UploadCloud, FileCheck2,
  Calculator, AlertCircle, CheckCircle2,
} from 'lucide-react';
import StatusBadge from './StatusBadge';
import { cn } from '@/lib/utils';

/**
 * Dynamic Form Renderer for HGS canonical schema.
 *
 * Schema shape:
 * {
 *   module, version,
 *   sections: [{
 *     sectionName,
 *     fields: [{
 *       fieldKey, label, type, required,
 *       options?, helpText?, sampleValue?, aiExamples?, formula?
 *     }]
 *   }]
 * }
 *
 * Supported field types:
 *  - text, textarea, dropdown, radio, date, number,
 *  - file_upload, calculated, ai_guidance, status_badge
 */

const STATUS_BADGE_VALUES = ['Low', 'Medium', 'High', 'Critical', 'Active', 'Inactive', 'Pending', 'Approved', 'Rejected', 'Green', 'Amber', 'Red'];

// ────────────────────────────────────────────────────────────────────────────
// Field renderers — one per type. Each is a pure function that takes the same
// props as FieldControl and returns JSX. The registry below dispatches.
// ────────────────────────────────────────────────────────────────────────────

const tid = (field) => `dyn-field-${field.fieldKey}`;

const renderTextarea = ({ value, onChange, field, base }) => (
  <textarea
    rows={3}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={field.sampleValue || ''}
    className={cn(base, 'resize-y')}
    data-testid={tid(field)}
  />
);

const renderDropdown = ({ value, onChange, field, base }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    className={base}
    data-testid={tid(field)}
  >
    <option value="">— Select —</option>
    {(field.options || []).map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

const renderRadio = ({ value, onChange, field }) => (
  <div className="flex flex-wrap gap-2" data-testid={tid(field)}>
    {(field.options || []).map((o) => {
      const sel = value === o;
      return (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={cn(
            'px-3 py-1.5 rounded-full border text-[11px] font-semibold transition',
            sel ? 'bg-[#CC0000] text-white border-[#CC0000] shadow-sm'
                : 'bg-white text-slate-700 border-slate-300 hover:border-[#CC0000]/50 hover:bg-red-50/40'
          )}
          data-testid={`dyn-radio-${field.fieldKey}-${o}`}
        >
          {o}
        </button>
      );
    })}
  </div>
);

const renderDate = ({ value, onChange, field, base }) => (
  <input
    type="date"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    className={base}
    data-testid={tid(field)}
  />
);

const renderNumber = ({ value, onChange, field, base }) => (
  <input
    type="number"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={field.sampleValue || ''}
    className={base}
    data-testid={tid(field)}
  />
);

const renderFileUpload = ({ value, onChange, field }) => {
  const fileName = value?.name || value || '';
  return (
    <label
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/40 hover:border-[#CC0000] hover:bg-red-50/30 cursor-pointer transition"
      data-testid={tid(field)}
    >
      {fileName ? (
        <>
          <FileCheck2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="text-[11px] text-slate-700 truncate flex-1 font-mono">{fileName}</span>
          <span className="text-[10px] font-semibold text-emerald-700">Uploaded</span>
        </>
      ) : (
        <>
          <UploadCloud className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <span className="text-[11px] text-slate-600 flex-1">Click to upload evidence (.pdf, .png, .jpg, .xlsx)</span>
          <span className="text-[10px] font-semibold text-[#CC0000]">Browse</span>
        </>
      )}
      <input
        type="file"
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.docx,.doc"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onChange(f.name); }}
      />
    </label>
  );
};

const renderCalculated = ({ field, allValues }) => {
  const calc = computeCalculated(field, allValues);
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200"
      data-testid={tid(field)}
    >
      <Calculator className="w-3.5 h-3.5 text-amber-700 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold text-amber-800">{calc.label}</div>
        {calc.detail && <div className="text-[9px] text-amber-700/80 truncate">{calc.detail}</div>}
      </div>
      {calc.value && <StatusBadge status={calc.value} dot={false} />}
    </div>
  );
};

const renderStatusBadge = ({ value, onChange, field }) => {
  const options = field.options && field.options.length ? field.options : STATUS_BADGE_VALUES;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={tid(field)}>
      {options.map((o) => {
        const sel = value === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={cn('transition', sel ? 'ring-2 ring-offset-1 ring-[#CC0000] rounded-full' : 'opacity-60 hover:opacity-100')}
            data-testid={`dyn-badge-${field.fieldKey}-${o}`}
          >
            <StatusBadge status={o} dot={true} />
          </button>
        );
      })}
    </div>
  );
};

const renderAIGuidance = ({ value, onChange, field }) => (
  <div
    className="rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50/40 p-3"
    data-testid={tid(field)}
  >
    <div className="flex items-center gap-1.5 mb-2">
      <Sparkles className="w-3.5 h-3.5 text-blue-700" />
      <span className="text-[10px] font-bold text-blue-900 uppercase tracking-wider">AI Guidance</span>
    </div>
    {field.aiExamples && field.aiExamples.length > 0 ? (
      <ul className="space-y-1.5">
        {field.aiExamples.map((ex, i) => (
          <li key={`ai-${i}-${(ex || '').slice(0, 20)}`} className="flex items-start gap-2 text-[11px] text-slate-700 leading-relaxed">
            <Lightbulb className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
            <span>{ex}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-[11px] text-slate-500 italic">AI suggestions appear here when this field is filled.</p>
    )}
    <textarea
      rows={2}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Add your own examples or notes..."
      className="mt-2 w-full px-2 py-1.5 text-[11px] bg-white border border-blue-200 rounded-md focus:outline-none focus:border-blue-500 resize-y"
    />
  </div>
);

const renderText = ({ value, onChange, field, base }) => (
  <input
    type="text"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={field.sampleValue || ''}
    className={base}
    data-testid={tid(field)}
  />
);

const FIELD_RENDERERS = {
  textarea:     renderTextarea,
  dropdown:     renderDropdown,
  radio:        renderRadio,
  date:         renderDate,
  number:       renderNumber,
  file_upload:  renderFileUpload,
  calculated:   renderCalculated,
  status_badge: renderStatusBadge,
  ai_guidance:  renderAIGuidance,
  text:         renderText,
};

function FieldControl({ field, value, onChange, allValues, error }) {
  const base = cn(
    'w-full px-3 py-2 text-xs bg-white border rounded-lg focus:outline-none focus:ring-2 transition',
    error
      ? 'border-[#CC0000] focus:border-[#CC0000] focus:ring-[#CC0000]/15'
      : 'border-slate-300 focus:border-[#CC0000] focus:ring-[#CC0000]/10 text-slate-800'
  );
  const renderer = FIELD_RENDERERS[field.type] || renderText;
  return renderer({ field, value, onChange, allValues, error, base });
}

/**
 * Calculated field engine.
 * Supports the demo formula `frequency*businessImpact` mapped to a band:
 *  freq score (1-5) × impact score (1-5) → 1-25; banded into Low/Medium/High/Critical
 */
function computeCalculated(field, all) {
  const FREQ_MAP = { 'Rare': 1, 'Unlikely': 2, 'Possible': 3, 'Likely': 4, 'Almost Certain': 5 };
  const IMP_MAP = { 'Insignificant': 1, 'Minor': 2, 'Moderate': 3, 'Major': 4, 'Catastrophic': 5 };
  if (field.formula === 'frequency*businessImpact') {
    const f = FREQ_MAP[all.frequency] || 0;
    const i = IMP_MAP[all.businessImpact] || 0;
    const score = f * i;
    if (!score) return { label: 'Pending — choose Frequency × Impact', detail: 'Auto-calculated risk score', value: null };
    let band = 'Low';
    if (score >= 17) band = 'Critical';
    else if (score >= 10) band = 'High';
    else if (score >= 5) band = 'Medium';
    return {
      label: `${score} / 25`,
      detail: `${all.frequency} (${f}) × ${all.businessImpact} (${i}) = ${score}`,
      value: band,
    };
  }
  return { label: field.sampleValue || 'Calculated', detail: field.formula || '—', value: null };
}

function isFieldVisible(field, allValues) {
  if (!field.showWhen) return true;
  const { fieldKey, valueIn, equals } = field.showWhen;
  const v = allValues[fieldKey];
  if (Array.isArray(valueIn)) return valueIn.includes(v);
  if (equals != null) return v === equals;
  return true;
}

function SectionBlock({ section, sectionIdx, values, setValues, errors }) {
  const [open, setOpen] = useState(sectionIdx === 0);

  const setVal = (key, v) => setValues((s) => ({ ...s, [key]: v }));
  const visibleFields = section.fields.filter((f) => isFieldVisible(f, values));
  const hiddenCount = section.fields.length - visibleFields.length;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white overflow-hidden"
      data-testid={`dyn-section-${sectionIdx}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-[#CC0000]/10 text-[#CC0000] flex items-center justify-center">
            <Layers className="w-3.5 h-3.5" />
          </div>
          <div className="text-left">
            <div className="text-sm font-bold text-slate-900">{section.sectionName}</div>
            <div className="text-[10px] text-slate-500">
              {visibleFields.length} of {section.fields.length} fields shown
              {hiddenCount > 0 && <span className="ml-1 text-amber-600">· {hiddenCount} conditional</span>}
              {section.rowCount ? ` · ${section.rowCount} master records` : ''}
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleFields.map((f) => {
            const fullWidth = f.type === 'textarea' || f.type === 'ai_guidance' || f.type === 'file_upload';
            const err = errors?.[f.fieldKey];
            return (
              <div
                key={f.fieldKey}
                className={cn(fullWidth ? 'md:col-span-2' : '', 'animate-in fade-in slide-in-from-top-1 duration-200')}
                data-testid={`dyn-field-wrap-${f.fieldKey}`}
              >
                <label className="text-[10px] font-semibold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  {f.label}
                  {f.required && <span className="text-[#CC0000]">*</span>}
                  {f.showWhen && (
                    <span className="ml-1.5 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[8px] font-bold">
                      CONDITIONAL
                    </span>
                  )}
                  <span className="ml-auto font-mono text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                    {f.type}
                  </span>
                </label>
                <FieldControl
                  field={f}
                  value={values[f.fieldKey]}
                  onChange={(v) => setVal(f.fieldKey, v)}
                  allValues={values}
                  error={err}
                />
                {f.helpText && !err && (
                  <p className="mt-1 text-[10px] text-slate-500 leading-relaxed flex items-start gap-1">
                    <span className="text-slate-400 mt-0.5">i</span>
                    {f.helpText}
                  </p>
                )}
                {err && (
                  <p className="mt-1 text-[10px] text-[#CC0000] flex items-center gap-1 font-medium" data-testid={`dyn-error-${f.fieldKey}`}>
                    <AlertCircle className="w-3 h-3" />
                    {err}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DynamicFormRenderer({ schema, onSubmit }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const allFields = useMemo(() => (schema?.sections || []).flatMap((s) => s.fields), [schema]);

  if (!schema || !schema.sections) {
    return (
      <div className="p-6 text-center text-xs text-slate-500">
        No schema provided.
      </div>
    );
  }

  const validate = () => {
    const errs = {};
    allFields.forEach((f) => {
      if (f.required && f.type !== 'calculated' && f.type !== 'ai_guidance') {
        if (!isFieldVisible(f, values)) return; // skip hidden conditional fields
        const v = values[f.fieldKey];
        if (v == null || (typeof v === 'string' && !v.trim())) {
          errs[f.fieldKey] = 'This field is required';
        }
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    const ok = validate();
    setSubmitted(true);
    if (ok && onSubmit) onSubmit(values);
  };

  const requiredVisible = allFields.filter((f) => f.required && f.type !== 'calculated' && f.type !== 'ai_guidance' && isFieldVisible(f, values));
  const requiredCount = requiredVisible.length;
  const filledRequired = requiredVisible.filter((f) => values[f.fieldKey] && (typeof values[f.fieldKey] !== 'string' || values[f.fieldKey].trim())).length;
  const completion = requiredCount === 0 ? 100 : Math.round((filledRequired / requiredCount) * 100);

  return (
    <div className="space-y-4" data-testid="dynamic-form-preview">
      <div className="rounded-lg bg-gradient-to-r from-red-50 to-amber-50 border border-red-100 px-4 py-3 flex items-center gap-3 flex-wrap">
        <Sparkles className="w-4 h-4 text-[#CC0000] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-slate-900">Live form rendered from JSON Schema</div>
          <div className="text-[10px] text-slate-600">
            Module: <span className="font-mono">{schema.module}</span> · {schema.version.startsWith('v') ? schema.version : `v${schema.version}`} · {schema.sections.length} section{schema.sections.length !== 1 ? 's' : ''} · {allFields.length} fields
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-[10px] text-slate-700">
            <span className="font-bold">{filledRequired}</span>/{requiredCount} required
          </div>
          <div className="w-24 h-1.5 rounded-full bg-white/70 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#CC0000] to-amber-500 transition-all"
              style={{ width: `${completion}%` }}
            />
          </div>
        </div>
      </div>

      {schema.sections.map((s, i) => (
        <SectionBlock
          key={s.sectionName + i}
          section={s}
          sectionIdx={i}
          values={values}
          setValues={setValues}
          errors={submitted ? errors : {}}
        />
      ))}

      <div className="flex items-center justify-between gap-3 px-1 pt-2">
        <div className="text-[10px] text-slate-500">
          This is a sandbox preview — values are not persisted.
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition shadow-sm"
          data-testid="dyn-form-submit"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          Validate & Submit
        </button>
      </div>

      {submitted && Object.keys(errors).length === 0 && (
        <div className="px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2" data-testid="dyn-form-success">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <div className="text-xs font-semibold text-emerald-900">Form validated — schema-driven submission OK.</div>
        </div>
      )}
    </div>
  );
}
